import { ref, watch, computed, nextTick, type Ref, type MaybeRefOrGetter, toValue } from 'vue'
import { deepEqual } from '@/utils/deepEqual'
import { useDirtyBeforeDismiss, type CloseConfirmVariant } from '@/composables/useDirtyBeforeDismiss'

function cloneJson<T>(v: T): T {
  try {
    return structuredClone(v)
  } catch {
    return JSON.parse(JSON.stringify(v)) as T
  }
}

/**
 * When the modal opens (`isOpen` truthy), snapshots `getState()` after nextTick (so reset-on-open runs first).
 */
export function useModalOpenBaselineGuard(
  isOpen: MaybeRefOrGetter<boolean>,
  getState: () => unknown,
  variant: CloseConfirmVariant = 'discard'
) {
  const baseline = ref<unknown>(null)

  watch(
    () => toValue(isOpen),
    (open) => {
      if (open) {
        // Double tick: parent reset-on-open + async microtasks (e.g. bug report autofill) settle first
        nextTick(() => {
          nextTick(() => {
            baseline.value = cloneJson(getState())
          })
        })
      }
    }
  )

  const isDirty = computed(() => {
    if (!toValue(isOpen) || baseline.value === null) return false
    return !deepEqual(getState(), baseline.value)
  })

  function syncBaseline() {
    baseline.value = cloneJson(getState())
  }

  const dirtyBeforeDismiss = useDirtyBeforeDismiss({ isDirty, variant })

  return Object.assign(dirtyBeforeDismiss, { syncBaseline })
}
